import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { api, ApiError } from "../../api";
import { Button, Card, Field } from "../../ui";

type Building = { id: string; name: string };
type Riser = { id: string; code: string; name: string };
type UnitOwnerSummary = {
  userId: string;
  name: string;
  email: string;
  ownershipType: string;
};
type UnitRow = {
  id: string;
  unitNumber: string;
  floor: number | null;
  unitType: string | null;
  location: string | null;
  isCombinedUnit: boolean;
  riserId: string;
  riserCode: string;
  riserName: string;
  /** Codes of any additional risers a combined unit spans. Empty
   *  array on regular units. The list cell renders these inline so
   *  combined units that cross risers read as e.g. "1, 2". */
  additionalRiserCodes: string[];
  hasWasherDryer: boolean;
  /** Active owners on this unit. Empty for unassigned units;
   *  multiple entries for joint ownership (spouses, partnerships).
   *  Surfaced as the Owners column so the admin can see at a
   *  glance who's already on each unit. */
  owners: UnitOwnerSummary[];
};

const UNIT_TYPES = [
  { value: "studio", label: "Studio" },
  { value: "one_bedroom", label: "1 Bedroom" },
  { value: "two_bedroom", label: "2 Bedroom" },
  { value: "three_bedroom", label: "3 Bedroom" },
] as const;

const LOCATIONS = [
  { value: "east", label: "East" },
  { value: "north", label: "North" },
  { value: "northeast_corner", label: "Northeast Corner" },
  { value: "northwest_corner", label: "Northwest Corner" },
  { value: "south", label: "South" },
  { value: "southeast_corner", label: "Southeast Corner" },
  { value: "southwest_corner", label: "Southwest Corner" },
  { value: "west", label: "West" },
] as const;

const TYPE_LABEL: Record<string, string> = Object.fromEntries(
  UNIT_TYPES.map((t) => [t.value, t.label]),
);
const LOCATION_LABEL: Record<string, string> = Object.fromEntries(
  LOCATIONS.map((l) => [l.value, l.label]),
);

export function AdminUnits() {
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [buildingId, setBuildingId] = useState<string>("");
  const [risers, setRisers] = useState<Riser[]>([]);
  const [units, setUnits] = useState<UnitRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    unitNumber: "",
    riserCode: "",
    unitType: "" as "" | (typeof UNIT_TYPES)[number]["value"],
    location: "" as "" | (typeof LOCATIONS)[number]["value"],
    isCombinedUnit: false,
    hasWasherDryer: false,
  });

  useEffect(() => {
    void api.get<{ data: Building[] }>("/api/buildings").then((r) => {
      setBuildings(r.data);
      if (r.data[0]) setBuildingId(r.data[0].id);
    });
  }, []);

  const reload = async (b = buildingId) => {
    if (!b) return;
    setLoading(true);
    try {
      const [u, r] = await Promise.all([
        api.get<{ data: UnitRow[] }>(`/api/admin/buildings/${b}/units`),
        api.get<{ data: Riser[] }>(`/api/buildings/${b}/risers`),
      ]);
      setUnits(u.data);
      setRisers(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (buildingId) void reload(buildingId);
  }, [buildingId]);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.riserCode) {
      setError("Choose a riser.");
      return;
    }
    try {
      await api.post(`/api/admin/buildings/${buildingId}/units`, {
        unitNumber: form.unitNumber,
        riserCode: form.riserCode,
        unitType: form.unitType || undefined,
        location: form.location || undefined,
        isCombinedUnit: form.isCombinedUnit,
        hasWasherDryer: form.hasWasherDryer,
      });
      setForm({
        unitNumber: "",
        riserCode: "",
        unitType: "",
        location: "",
        isCombinedUnit: false,
        hasWasherDryer: false,
      });
      await reload();
    } catch (err) {
      if (err instanceof ApiError) {
        const code = (err.body as { error?: string })?.error ?? "create_failed";
        setError(
          code === "unit_number_taken"
            ? "A unit with that number already exists."
            : code === "riser_not_found"
              ? "Unknown riser."
              : `Create failed: ${code}`,
        );
      } else setError("Create failed.");
    }
  };

  const toggleWD = async (u: UnitRow, next: boolean) => {
    await api.patch(`/api/admin/units/${u.id}`, { hasWasherDryer: next });
    await reload();
  };

  return (
    <div>
      <Card className="!p-0 overflow-hidden">
        <div className="flex items-center justify-between p-5 sm:p-6 border-b border-ink-100">
          <div>
            <h2 className="text-ink-800 m-0">Units</h2>
            <p className="text-xs text-ink-500 mt-0.5">
              Toggle the W/D column to update both the unit and the riser counter.
            </p>
          </div>
          <select
            className="select max-w-xs"
            value={buildingId}
            onChange={(e) => setBuildingId(e.target.value)}
          >
            {buildings.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <p className="p-6 text-ink-500 text-sm">Loading…</p>
        ) : units.length === 0 ? (
          <p className="p-6 text-ink-500 text-sm">No units yet.</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Unit</th>
                  <th>Riser</th>
                  <th>Type</th>
                  <th>Location</th>
                  <th>Owners</th>
                  <th>Combined</th>
                  <th>W/D</th>
                </tr>
              </thead>
              <tbody>
                {units.map((u) => (
                  <tr key={u.id}>
                    <td className="font-medium text-ink-800">
                      <Link
                        to={`/admin/units/${u.id}`}
                        className="text-ink-800 hover:text-brand-700 no-underline hover:no-underline"
                        title="Open this unit's detail page"
                      >
                        {u.unitNumber}
                      </Link>
                    </td>
                    <td className="text-ink-600">
                      <span className="font-mono">
                        {[u.riserCode, ...u.additionalRiserCodes].join(", ")}
                      </span>
                      <span className="text-ink-400"> · {u.riserName}</span>
                    </td>
                    <td className="text-ink-600">
                      {u.unitType ? TYPE_LABEL[u.unitType] : "—"}
                    </td>
                    <td className="text-ink-600">
                      {u.location ? LOCATION_LABEL[u.location] : "—"}
                    </td>
                    <td>
                      {u.owners.length === 0 ? (
                        <span className="text-ink-400 text-sm">
                          unassigned
                        </span>
                      ) : (
                        <div className="flex flex-col gap-0.5">
                          {u.owners.map((o) => (
                            <span
                              key={o.userId}
                              className="text-[12.5px]"
                              title={`${o.email} · ${o.ownershipType.replace("_", "-")}`}
                            >
                              <Link
                                to={`/admin/users/${o.userId}`}
                                className="text-ink-700 hover:text-brand-700 no-underline hover:no-underline"
                              >
                                {o.name}
                              </Link>
                              {o.ownershipType !== "primary" && (
                                <span className="text-[10.5px] text-ink-500 ml-1">
                                  ({o.ownershipType.replace("_", "-")})
                                </span>
                              )}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td>
                      {u.isCombinedUnit ? (
                        <span className="badge bg-status-approved-bg text-status-approved-fg">
                          yes
                        </span>
                      ) : (
                        <span className="text-ink-400">—</span>
                      )}
                    </td>
                    <td>
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={u.hasWasherDryer}
                          onChange={(e) => void toggleWD(u, e.target.checked)}
                          className="rounded border-ink-300 text-brand-700 focus:ring-brand-500"
                        />
                        <span className="text-sm">
                          {u.hasWasherDryer ? "yes" : "no"}
                        </span>
                      </label>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card>
        <h3 className="text-ink-800 m-0 mb-3">Add a unit</h3>
        <form onSubmit={create}>
          <div className="field-row">
            <Field label="Unit number">
              <input
                className="input"
                value={form.unitNumber}
                onChange={(e) => setForm({ ...form, unitNumber: e.target.value })}
                placeholder="e.g. 1108"
                required
              />
            </Field>
            <Field label="Riser">
              <select
                className="select"
                value={form.riserCode}
                onChange={(e) => setForm({ ...form, riserCode: e.target.value })}
                required
              >
                <option value="">Select riser…</option>
                {risers.map((r) => (
                  <option key={r.id} value={r.code}>
                    {r.code} — {r.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="field-row">
            <Field label="Type">
              <select
                className="select"
                value={form.unitType}
                onChange={(e) =>
                  setForm({
                    ...form,
                    unitType: e.target
                      .value as (typeof UNIT_TYPES)[number]["value"] | "",
                  })
                }
                required
              >
                <option value="">Select type…</option>
                {UNIT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Location">
              <select
                className="select"
                value={form.location}
                onChange={(e) =>
                  setForm({
                    ...form,
                    location: e.target
                      .value as (typeof LOCATIONS)[number]["value"] | "",
                  })
                }
                required
              >
                <option value="">Select location…</option>
                {LOCATIONS.map((l) => (
                  <option key={l.value} value={l.value}>
                    {l.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mb-3">
            <label className="flex items-center gap-2 text-sm text-ink-700 cursor-pointer">
              <input
                type="checkbox"
                checked={form.isCombinedUnit}
                onChange={(e) =>
                  setForm({ ...form, isCombinedUnit: e.target.checked })
                }
                className="rounded border-ink-300 text-brand-700 focus:ring-brand-500"
              />
              Combined unit
            </label>
            <label className="flex items-center gap-2 text-sm text-ink-700 cursor-pointer">
              <input
                type="checkbox"
                checked={form.hasWasherDryer}
                onChange={(e) =>
                  setForm({ ...form, hasWasherDryer: e.target.checked })
                }
                className="rounded border-ink-300 text-brand-700 focus:ring-brand-500"
              />
              Existing washer/dryer hookup
            </label>
          </div>
          {error && <div className="alert-error">{error}</div>}
          <Button variant="primary" type="submit">
            <Plus size={16} /> Add unit
          </Button>
        </form>
      </Card>
    </div>
  );
}
